import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { HiOutlineFunnel } from "react-icons/hi2";
import { roleAccess, usePermissionsVersion } from "helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import "./invoicing-workflow.css";

export const INVOICING_WORKFLOW_STEPS = [
  {
    id: "allocation",
    step: 1,
    title: "Allocate codes",
    subtitle: "Assign NDIS items",
    path: "/rostering/invoicing/allocation",
    permissionChild: PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.ALLOCATION,
  },
  {
    id: "create-invoice",
    step: 2,
    title: "Create Invoice",
    subtitle: "Review & extract",
    path: "/rostering/create-roster-invoice",
    permissionChild: PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.CREATE,
  },
  {
    id: "invoices",
    step: 3,
    title: "Invoices",
    subtitle: "Manage & export",
    path: "/rostering/invoicing",
    permissionChild: PERMISSION_KEYS_CHILDREN.ROSTER_INVOICE.CREATE,
  },
];

export const getVisibleInvoicingWorkflowSteps = () =>
  INVOICING_WORKFLOW_STEPS.filter((step) =>
    roleAccess([], PERMISSION_KEYS.ROSTER_INVOICE, step.permissionChild)
  );

export const InvoicingWorkflowStepper = ({ activeStepId }) => {
  const navigate = useNavigate();
  const permissionsVersion = usePermissionsVersion();
  const visibleSteps = useMemo(
    () => getVisibleInvoicingWorkflowSteps(),
    [permissionsVersion]
  );
  const activeIndex = visibleSteps.findIndex((s) => s.id === activeStepId);

  return (
    <div className="iw-stepper" aria-label="Invoicing workflow">
      {visibleSteps.map((step, index) => {
        const isActive = step.id === activeStepId;
        const isComplete = activeIndex > index;
        const isLink = !isActive;

        return (
          <React.Fragment key={step.id}>
            {index > 0 && (
              <span
                className={`iw-step-connector${
                  activeIndex >= index ? " is-complete" : ""
                }`}
                aria-hidden
              />
            )}
            <button
              type="button"
              className={`iw-step${isActive ? " is-active" : ""}${
                isComplete ? " is-complete" : ""
              }${isLink ? " is-link" : ""}`}
              onClick={isLink ? () => navigate(step.path) : undefined}
              aria-current={isActive ? "step" : undefined}
            >
              <span className="iw-step-num">{step.step}</span>
              <span className="iw-step-copy">
                <span className="iw-step-title">{step.title}</span>{" "}
                <span className="iw-step-sub">{step.subtitle}</span>
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const InvoicingWorkflowStatCard = ({
  icon: Icon,
  value,
  label,
  subValue = null,
  progress = null,
  highlight = false,
  iconVariant = "blue",
  valueClassName = "",
  labelClassName = "",
}) => (
  <div
    className={`iw-stat-card iw-stat-card--${iconVariant}${
      highlight ? " is-highlight" : ""
    }`}
  >
    <span className={`iw-stat-icon iw-stat-icon--${iconVariant}`}>
      <Icon className="h-5 w-5" aria-hidden />
    </span>
    <div className="iw-stat-copy">
      <div className={`iw-stat-value${valueClassName ? ` ${valueClassName}` : ""}`}>
        {value ?? 0}
      </div>
      <div className={`iw-stat-label${labelClassName ? ` ${labelClassName}` : ""}`}>
        {label}
      </div>
      {subValue ? <div className="iw-stat-sub">{subValue}</div> : null}
      {progress != null ? (
        <div
          className="iw-stat-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} progress`}
        >
          <div
            className="iw-stat-progress-fill"
            style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
          />
        </div>
      ) : null}
    </div>
  </div>
);

export const InvoicingWorkflowFilterField = ({ label, children }) => (
  <div>
    {label && <span className="iw-filter-field-label">{label}</span>}
    {children}
  </div>
);

const InvoicingWorkflowLayout = ({
  activeStepId,
  stats = [],
  headerActions = null,
  filterTitle = "Filter shifts",
  filterActions = null,
  filterGridClass = "iw-filter-grid--3",
  filters = null,
  children,
}) => (
  <div className="invoicing-workflow-page iw-page-body">
    <div className="iw-stepper-bar">
      <InvoicingWorkflowStepper activeStepId={activeStepId} />
    </div>

    {stats.length > 0 && (
      <div className="iw-stats">
        {stats.map((stat) => (
          <InvoicingWorkflowStatCard key={stat.label} {...stat} />
        ))}
      </div>
    )}

    {filters && (
      <div className="iw-filter-card">
        <div className="iw-filter-header">
          <h2 className="iw-filter-title">
            <HiOutlineFunnel className="iw-filter-title-icon" aria-hidden />
            {filterTitle}
          </h2>
          {(headerActions || filterActions) && (
            <div className="iw-filter-header-actions">
              {headerActions}
              {filterActions && (
                <div className="iw-bulk-actions">{filterActions}</div>
              )}
            </div>
          )}
        </div>
        <div className={`iw-filter-grid ${filterGridClass}`}>{filters}</div>
      </div>
    )}

    <div className="iw-content">{children}</div>
  </div>
);

export default InvoicingWorkflowLayout;
